<script setup lang="ts">
import { Button, ZhSystemLabel } from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api, type Me, type OnboardingStateView, type ToolBindingRow } from '@/api'
import ExpressToolDirectory from '@/components/ExpressToolDirectory.vue'
import { GUIDED_SETUP_PATH } from '@/lib/guidedSetup'
import {
    MORE_TOOLS_PATH,
    MORE_TOOLS_SKIP,
    MORE_TOOLS_STEP,
    MORE_TOOLS_TITLE,
    rememberStartedTool,
    signInDidNotFinish,
    takeStartedTool,
} from '@/lib/moreTools'
import { onboardingSession } from '@/lib/onboardingSession'

/**
 * The optional "Connect more of your tools" step (#1942), at /onboarding/more-tools and inside the guided shell once
 * Team setup is saved. It reads the server-held setup state on the guided path, the same state the COO's chat reads,
 * and reuses the Tools page's Express directory through its opt-in props: nothing here is a copy of it.
 *
 * - The company owner connects company-wide (the enterprise layer), with no "who uses it?" question, and the sign-in
 *   comes back here. Anyone else sees the directory read-only (#2264 adds a personal connection).
 * - The tools the team needs are listed with the status Team setup gives them, and are not offered again.
 * - Skip sits above the directory, so it is visible without scrolling, and says what it does. It saves the skip in the
 *   setup state, and works whether or not the directory loaded. Continue saves the step as done.
 * - Coming back from a sign-in, the state is read again; one that did not finish is said in one plain sentence,
 *   naming the tool this tab started.
 */
const emit = defineEmits<{
    /** The step was skipped or done: whatever holds it reads setup again. */
    finished: []
}>()

const route = useRoute()
const router = useRouter()
const session = onboardingSession()

const state = ref<OnboardingStateView | null>(null)
const me = ref<Me | null>(null)
const rows = ref<ToolBindingRow[]>([])
/** Who is reading is known (or could not be), so the directory can be drawn for them. */
const ready = ref(false)
const failure = ref<string | null>(null)
const signInFailure = ref<string | null>(null)
const saving = ref(false)

const isOwner = computed(() => me.value?.isOwner === true)
const enterpriseId = computed(() => me.value?.enterprise.id ?? '')
const needs = computed(() => state.value?.moreTools?.needs ?? [])

async function loadState(): Promise<void> {
    try {
        state.value = await api.onboarding('guided', session)
    } catch {
        failure.value = 'Could not load where setup is. Reload the page to try again.'
    }
}

async function loadMe(): Promise<void> {
    try {
        me.value = await api.me()
    } catch {
        // Not knowing who is reading draws the directory read-only.
        me.value = null
    }
}

/** The company-wide connections, so a tool connected for everyone shows as Connected. */
async function loadRows(): Promise<void> {
    if (!enterpriseId.value) {
        return
    }
    try {
        rows.value = await api.toolBindings({ layer: 'enterprise', layerId: enterpriseId.value })
    } catch {
        rows.value = []
    }
}

onMounted(async () => {
    const started = takeStartedTool()
    if (route.query['oauth_error']) {
        signInFailure.value = signInDidNotFinish(started)
    }
    await Promise.all([loadState(), loadMe()])
    await loadRows()
    ready.value = true
})

/** Saves the step as skipped or done in the setup state, then moves on in the guided steps. */
async function finish(answers: { skipped: true } | { done: true }): Promise<void> {
    saving.value = true
    failure.value = null
    try {
        state.value = await api.saveOnboardingStep(MORE_TOOLS_STEP, answers, 'guided', session)
        emit('finished')
        await router.push(GUIDED_SETUP_PATH)
    } catch (error) {
        failure.value = error instanceof Error ? error.message : 'Could not save this step. Try again.'
    } finally {
        saving.value = false
    }
}
</script>

<template>
    <section class="flex w-full flex-col gap-4 p-6" data-testid="onboarding-more-tools">
        <ZhSystemLabel tone="muted">Optional step</ZhSystemLabel>
        <h1 class="text-xl font-semibold" data-testid="more-tools-title">{{ MORE_TOOLS_TITLE }}</h1>
        <p v-if="isOwner" class="text-sm" data-testid="more-tools-intro">
            Pick a tool and sign in with your own account. A tool you connect here is connected for the whole company.
        </p>
        <p v-else class="text-sm" data-testid="more-tools-intro">
            Your account owner connects tools for the whole company. You can see what is available and carry on.
        </p>
        <div class="flex flex-wrap items-center gap-3">
            <Button type="button" variant="outline" :disabled="saving" data-testid="more-tools-skip" @click="finish({ skipped: true })">{{ MORE_TOOLS_SKIP }}</Button>
            <Button
                v-if="state?.moreTools"
                type="button"
                :disabled="saving"
                data-testid="more-tools-done"
                @click="finish({ done: true })"
            >
                Continue
            </Button>
        </div>
        <p v-if="signInFailure" role="alert" class="text-destructive text-sm" data-testid="more-tools-sign-in-failure">
            {{ signInFailure }}
        </p>
        <p v-if="failure" role="alert" class="text-destructive text-sm" data-testid="more-tools-failure">{{ failure }}</p>
        <ExpressToolDirectory
            v-if="ready"
            :enterprise-id="enterpriseId"
            :teams="[]"
            :members="[]"
            :rows="rows"
            :lock-layer="isOwner ? 'enterprise' : undefined"
            :lock-layer-id="isOwner ? enterpriseId : undefined"
            :return-to="MORE_TOOLS_PATH"
            :read-only="!isOwner"
            step-mode
            :needs="needs"
            @connecting="tool => rememberStartedTool(tool.name)"
        />
        <p class="text-muted-foreground text-sm" data-testid="more-tools-not-listed">
            Don't see your tool? You can add it later from the Tools page.
        </p>
        <p class="text-muted-foreground text-sm" data-testid="more-tools-disconnect">
            To disconnect a tool, go to the Tools page.
        </p>
    </section>
</template>
